import React, { useCallback, useMemo } from 'react';
import { rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, } from 'design-system/values';
import ListEmptyView from 'design-system/ListEmptyView';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import SectionHeader from 'design-system/SectionHeader';
import { AppView, SettingsItem } from 'design-system';
import IconPlus from 'design-system/icons/IconPlus';
import useColors from 'hooks/useColors';
import { FlatList, View } from 'react-native';
import Button from 'design-system/Button';
import { useMarginBottom } from 'hooks/useMarginBottom';
import links from 'utils/links';
import Text from 'design-system/Text';
import { AnimatedFlashList } from '@shopify/flash-list';
import FlatListItemCard from 'design-system/FlatListItemCard';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { isWeb } from '../../../constants';
import { AccountItem } from '../components';
import { useSpaceAccounts } from '../hooks/useSpaceAccounts';
import { useAddSpaceAccountModal } from '../hooks/useAddSpaceAccountModal';
const PrimaryAccountsPage = ({ spaceInfo, onScroll }) => {
    const { defaultSpaceAccounts: accounts } = useSpaceAccounts(spaceInfo);
    const styles = useStyles(styleSet);
    const colors = useColors();
    const onAddAccount = useAddSpaceAccountModal(spaceInfo, true);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const onPressInfo = useCallback(() => {
        links.openKnownLink('spacesHelp');
    }, []);
    const keyExtractor = useCallback((item) => item.id.toString(), []);
    const addAccounts = useMemo(() => (<FlatListItemCard index={0} itemsLength={accounts.length + 1} style={styles.card}>
        {<SettingsItem onPress={onAddAccount} title="Add account" renderIcon={() => (<View style={styles.addNewSpace}>
                <IconPlus color={colors.text.brand}/>
              </View>)}/>}
      </FlatListItemCard>), [
        accounts.length,
        colors.text.brand,
        onAddAccount,
        styles.addNewSpace,
        styles.card,
    ]);
    const renderHeader = useMemo(() => (<>
        <View style={styles.textContainer}>
          <Text testID="private-space-text" TextThin-16 Secondary>
            Your private space can’t be deleted and it’s your default space.{' '}
            <Text onPress={onPressInfo} Text-16 Primary suppressHighlighting>
              Learn more
            </Text>
          </Text>
        </View>
        <SectionHeader horizontalMargins largeVerticalMargins>
          Accounts
        </SectionHeader>
        {accounts.length > 0 ? addAccounts : null}
      </>), [accounts.length, addAccounts, onPressInfo, styles.textContainer]);
    const renderEmpty = useMemo(() => (<ListEmptyView style={styles.emptyContainer} compact title="No accounts" subTitle="You don’t have any accounts in this space">
        <Button brandReversed hug medium onPress={onAddAccount} title="Add account"/>
      </ListEmptyView>), [onAddAccount, styles.emptyContainer]);
    const renderItem = useCallback(({ item, index }) => {
        const updatedIndex = index + 1;
        return (<FlatListItemCard index={updatedIndex} itemsLength={accounts.length + 1} style={styles.card}>
          <AccountItem disabled account={item}/>
        </FlatListItemCard>);
    }, [accounts.length, styles.card]);
    const baseProps = {
        scrollEventThrottle: 16,
        scrollIndicatorInsets: SCROLL_INDICATOR_INSETS,
        ListHeaderComponent: renderHeader,
        renderItem,
        ListEmptyComponent: renderEmpty,
        data: accounts,
        keyExtractor,
        contentContainerStyle,
        onScroll,
    };
    return (<AppView withFrame={false} type="secondary">
      {isWeb ? (<FlatList {...baseProps}/>) : (<AnimatedFlashList {...baseProps}/>)}
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    emptyContainer: {
        padding: rem(24),
        backgroundColor: colors.cards.onDark,
        marginHorizontal: rem(16),
        borderRadius: rem(16),
    },
    addNewSpace: {
        width: rem(44),
        height: rem(44),
        borderRadius: rem(50),
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: colors.background.dark,
        marginRight: rem(16),
    },
    container: {
        width: '100%',
        flex: 1,
        alignItems: 'stretch',
    },
    textContainer: {
        marginTop: rem(16),
        marginHorizontal: rem(16),
    },
    card: {
        backgroundColor: colors.cards.onDark,
    },
}));
export default PrimaryAccountsPage;
